/**
 * Shared Tiptap Extension Profiles
 *
 * This module defines canonical extension sets used across multiple surfaces
 * to ensure schema consistency. Changes here affect all consumers.
 *
 * ## Profiles
 *
 * ### TEXT_EXTRACTION_EXTENSIONS
 * Used by: CharacterSchema.js, WorldbuildingEntrySchema.js
 * Purpose: Server-side text extraction from Tiptap JSON (via `generateText`).
 * Contains the minimal set of extensions needed to parse stored document content.
 *
 * ### FULL_DOCUMENT_EXTENSIONS
 * Used by: getProseMirrorSchema.js (server-side schema generation)
 * Purpose: The canonical document schema for ProseMirror node/mark validation.
 * This is used for stripping invalid nodes/marks before persistence.
 *
 * ## Intentional Deviations (NOT covered by these profiles)
 *
 * - History Viewer: Omits Collaboration (reads snapshots, not live docs)
 * - DraftComparison: Minimal schema for diff rendering, including Link/Image
 *   but no collaboration or tracked-change/annotation marks.
 * - TiptapField: Reduced form input schema with list support, no Link/Image.
 * - TiptapListField: Custom list-only schema for structured form inputs
 * - simple-tiptap-editor: Secondary HTML/JSON serializer with RN-friendly
 *   Bold/Italic tags and insertion/deletion marks.
 * - Client-side editors (Editor.js, ReadOnlyEditor.js, tiptapEditor.js):
 *   Use ThemeAwareHighlight instead of Highlight, plus additional UI extensions
 *   (Placeholder, Focus, Dropcursor, etc.) not needed server-side.
 *
 * ## Adding a document-level node type
 *
 * Add it here AND to every hand-maintained reader list at the same time:
 * src/tiptapEditor.js, client Editor.js, both lists in client ReadOnlyEditor.js,
 * History preview (historyContent.js), DraftComparison, plus the mobile editor
 * and ws-tiptap import/export. A reader that lacks the node deletes it from the
 * shared document. See the tiptap-schema-sync skill for the full inventory.
 */

import Inserted from "@sudowrite/extension-inserted";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Heading from "@tiptap/extension-heading";
import Strike from "@tiptap/extension-strike";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextStyle from "@tiptap/extension-text-style";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Typography from "@tiptap/extension-typography";
import Collaboration from "@tiptap/extension-collaboration";
import History from "@tiptap/extension-history";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { InsertionMark, DeletionMark } from "../trackChanges";
import TrackChanges from "../trackChanges";
import Annotation from "../annotation";

/**
 * Extensions for server-side text extraction from Tiptap JSON.
 * Used by CharacterSchema and WorldbuildingEntrySchema to extract
 * plain text from stored TipTap JSON for validation and indexing.
 */
export const TEXT_EXTRACTION_EXTENSIONS = [
  Document,
  Paragraph,
  Text,
  HardBreak,
  Bold,
  Italic,
  Heading,
  Strike,
  Underline,
  Highlight,
  TextStyle,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  InsertionMark,
  DeletionMark,
];

/**
 * The canonical full document extension set for server-side schema generation.
 * Used by getProseMirrorSchema.js for ProseMirror schema validation
 * (stripping invalid nodes/marks before persistence).
 *
 * Client-side editors add UI-only extensions (Placeholder, Focus, Dropcursor,
 * ThemeAwareHighlight) on top of this base, which is why they don't import
 * this profile directly.
 */
export const FULL_DOCUMENT_EXTENSIONS = [
  Collaboration,
  Document,
  Paragraph,
  Typography,
  Text,
  Bold,
  Italic,
  Heading,
  Underline,
  HardBreak,
  Strike,
  Highlight.configure({
    multicolor: true,
  }),
  BulletList,
  ListItem,
  // Checklists (SW-10349). Every reader must know these nodes: a Tiptap client
  // whose schema lacks them deletes them from the shared Yjs document on connect,
  // and stripInvalidNodes would rewrite them as paragraphs. Only *creating* a
  // checklist is gated (Subscription.hasAccessToChecklists); reading never is.
  TaskList,
  TaskItem.configure({ nested: true }),
  Inserted,
  Link,
  Image,
  History,
  Annotation,
  TrackChanges,
];
